function c_scrollToSection(target) {
  let corrected = false;
  const correct = () => {
    if (corrected) return;
    corrected = true;
    const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    if (Math.abs(target.getBoundingClientRect().top - margin) > 4) target.scrollIntoView({ block: 'start' });
  };

  // Sections below the fold grow as they load, which stops a smooth scroll short of the target.
  window.addEventListener('scrollend', correct, { once: true });
  setTimeout(correct, 1200);
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

class CShopMegaMenu extends HTMLElement {
  connectedCallback() {
    this.c_item = this.closest('.menu-list__list-item');
    this.c_submenu = this.closest('.c-smm-submenu');
    this.c_trigger = this.c_item?.querySelector('[data-c-smm-trigger]');
    this.c_categories = [...this.querySelectorAll('[data-c-smm-cat]')];
    this.c_active = null;
    this.c_overlay = document.querySelector('.c-smm-page-overlay');
    if (!this.c_overlay) {
      this.c_overlay = document.createElement('div');
      this.c_overlay.className = 'c-smm-page-overlay';
      this.c_overlay.setAttribute('aria-hidden', 'true');
      document.body.append(this.c_overlay);
    }

    this.#reset();

    this.addEventListener('pointerover', this.#onCategoryEnter);
    this.addEventListener('focusin', this.#onCategoryEnter);
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('resize', this.#onResize);

    if (this.c_trigger) {
      this.c_observer = new MutationObserver(() => {
        if (this.#isOpen()) {
          this.setAttribute('data-c-smm-opened', '');
          this.#fitToViewport();
          this.c_overlay?.setAttribute('data-open', '');
          return;
        }
        this.c_overlay?.removeAttribute('data-open');
        this.#reset();
        if (this.c_submenu?.hasAttribute('data-c-smm-dismissed')) {
          // Hover state is only recalculated on the next pointer move, so the panel stays hidden until then.
          document.addEventListener('pointermove', () => this.c_submenu?.removeAttribute('data-c-smm-dismissed'), {
            once: true,
          });
        }
      });
      this.c_observer.observe(this.c_trigger, { attributes: true, attributeFilter: ['aria-expanded'] });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('resize', this.#onResize);
    this.c_observer?.disconnect();
    this.c_overlay?.removeAttribute('data-open');
  }

  // The submenu is positioned inside the centred header row; this stretches it, and its hover area, to the viewport edges.
  #fitToViewport() {
    const row = this.c_submenu?.offsetParent;
    if (!row) return;

    // Written to a page-level style, not the submenu: the header's hydration morph resets the submenu's inline style.
    this.c_fit ||= document.head.appendChild(document.createElement('style'));
    this.c_fit.textContent = `.c-smm-submenu { --c-smm-offset: ${row.getBoundingClientRect().left}px; --c-smm-width: ${document.documentElement.clientWidth}px; }`;
    this.c_overlay?.style.setProperty('--c-smm-overlay-top', `${row.getBoundingClientRect().bottom}px`);
  }

  #onResize = () => {
    if (this.#isOpen()) this.#fitToViewport();
  };

  #isOpen() {
    return this.c_trigger?.getAttribute('aria-expanded') === 'true';
  }

  #reset() {
    this.#setActive(this.c_categories[0]);
  }

  #setActive(category) {
    if (!category || category === this.c_active) return;

    this.c_active?.removeAttribute('data-active');
    category.setAttribute('data-active', '');
    this.c_active = category;
  }

  #onCategoryEnter = (event) => {
    const row = event.target instanceof Element ? event.target.closest('[data-c-smm-row]') : null;
    if (row) this.#setActive(row.closest('[data-c-smm-cat]'));
  };

  #close(returnFocus) {
    if (returnFocus) this.c_trigger?.focus();

    // header-menu will not close while the pointer is over the menu, so hide the panel first and retry until it lets go.
    this.c_submenu?.setAttribute('data-c-smm-dismissed', '');
    let attempts = 0;
    const tryClose = () => {
      if (!this.#isOpen() || attempts++ > 30) return;
      this.c_item?.dispatchEvent(new PointerEvent('pointerleave'));
      requestAnimationFrame(tryClose);
    };
    requestAnimationFrame(tryClose);
  }

  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#isOpen()) return;

    event.preventDefault();
    this.#close(true);
  };

  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-c-smm-home-anchor]') : null;
    if (!link) return;

    const target = document.getElementById(link.getAttribute('data-c-smm-home-anchor') ?? '');
    if (!target) return;

    event.preventDefault();
    this.#close(false);
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    c_scrollToSection(target);
    history.replaceState(null, '', link.getAttribute('href'));
  };
}

if (!customElements.get('c-shop-mega-menu')) {
  customElements.define('c-shop-mega-menu', CShopMegaMenu);
}

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('header-drawer [data-c-smm-home-anchor]') : null;
  if (!link) return;

  const target = document.getElementById(link.getAttribute('data-c-smm-home-anchor') ?? '');
  const drawer = link.closest('header-drawer');
  if (!target || !drawer) return;

  event.preventDefault();

  drawer.close();

  // The drawer keeps the page scroll-locked until its close animation has finished.
  const started = performance.now();
  const scrollWhenUnlocked = () => {
    if (document.documentElement.hasAttribute('scroll-lock') && performance.now() - started < 2000) {
      requestAnimationFrame(scrollWhenUnlocked);
      return;
    }
    c_scrollToSection(target);
    history.replaceState(null, '', link.getAttribute('href'));
  };
  requestAnimationFrame(scrollWhenUnlocked);
});

const c_announcement = document.querySelector('[data-c-smm-announcement]');
const c_announcementSource = [...document.querySelectorAll('.announcement-bar__text')].find((el) => el.textContent.trim());
// Kept in an attribute: the header's hydration morph resets this element's children but not its attributes.
if (c_announcement && c_announcementSource) {
  c_announcement.setAttribute('data-c-smm-text', c_announcementSource.textContent.trim());
  c_announcement.hidden = false;
}

// The chat widget's host sets its own inline !important styles, so only an inline style can hide it while the mobile menu is open.
const c_drawer = document.querySelector('header-drawer');
if (c_drawer) {
  const c_syncChat = () => {
    const host = document.getElementById('phntm-widget-host');
    if (!host) return;
    const open = Boolean(c_drawer.querySelector('details.menu-open'));
    host.style.setProperty('display', open ? 'none' : 'initial', 'important');
  };
  new MutationObserver(c_syncChat).observe(c_drawer, { subtree: true, attributes: true, attributeFilter: ['class'] });
}
